/** ActionQueue — prioritised list of "do now" items across tasks, applications,
 *  tenders, site cards and LOLER exams. Each row opens the thing it names, through the app:navigate bridge
 *  App.tsx answers: an application lands on its project's Applications tab, a
 *  tender on its project, a task on the task itself. */

import { useId, useMemo, useState } from 'react';
import type { Project, ContractItem, InterimApplication } from '../../types/database';
import type { Task } from '../../types/tasks';
import type { ExpiringWorkerCard } from '../../types/workerCards';
import type { ExpiringSubcontractorCover } from '../subcontractors/subcontractorCover';
import type { PlantAsset } from '../../types/plant';
import type { CISReturn, ExpenseClaim, Invoice } from '../../types/accounting';
import type { InvoiceChaseStamp } from './overdueInvoiceRow';
import type { UnsignedDayworkTally } from '../../contract-administration/dayworkSignOff';
import type { RfiRecord } from '../../types/rfis';
import type { WorkspaceDeal } from '../pipeline/types';
import { openPayrollSection } from '../../utils/payrollSectionIntent';
import { openPlantAsset } from '../../utils/plantSelectIntent';
import { openPurchasesSection } from '../../utils/purchasesSectionIntent';
import { openSalesInvoices } from '../../services/SalesSummaryService';
import { useComingUpMilestonesPreference } from '../../hooks/useComingUpMilestonesPreference';
import { useVariationChases } from './useVariationChases';
import { buildActionItems, projectsWithProcurementAccess, type ActionTarget, type SnagTally } from './actionQueueItems';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { ComingUpList } from '../../shared-ui/home/ComingUpList';
import { comingUpDueCue, comingUpSections } from '../../shared-ui/home/homeModels';

function openTarget(target: ActionTarget): void {
  if (target.view === 'payroll-employee') {
    openPayrollSection('employees', target.employeeId);
    return;
  }
  if (target.view === 'payroll-subcontractors') {
    openPayrollSection('subcontractors');
    return;
  }
  if (target.view === 'assets' && target.assetId) {
    openPlantAsset(target.assetId);
    return;
  }
  if (target.view === 'sales' && target.invoiceFilter) {
    openSalesInvoices(target.invoiceFilter);
    return;
  }
  if (target.view === 'purchases') {
    openPurchasesSection(target.section);
    return;
  }
  window.dispatchEvent(new CustomEvent('app:navigate', { detail: target }));
}

interface ActionQueueProps {
  tasks: Task[];
  contractItems: ContractItem[];
  applications: InterimApplication[];
  projects: Project[];
  siteCards?: readonly ExpiringWorkerCard[];
  subcontractorCover?: readonly ExpiringSubcontractorCover[];
  plantAssets?: readonly PlantAsset[];
  cisReturns?: readonly CISReturn[];
  expenseClaims?: readonly ExpenseClaim[];
  salesInvoices?: readonly Invoice[];
  invoiceChases?: readonly InvoiceChaseStamp[];
  unsignedDayworks?: Readonly<Record<string, UnsignedDayworkTally>>;
  outstandingSnags?: Readonly<Record<string, SnagTally>>;
  rfis?: readonly RfiRecord[];
  enquiryDeals?: readonly WorkspaceDeal[];
  /** Whose milestone choice to load and save. */
  userId: string;
  maxRows?: number;
  windowDays?: number;
  /** Field-only members see tasks and site milestones, not commercial dates. */
  siteOnly?: boolean;
}

export function ActionQueue({
  tasks,
  contractItems,
  applications,
  projects,
  siteCards,
  subcontractorCover,
  plantAssets,
  cisReturns,
  expenseClaims,
  salesInvoices,
  invoiceChases,
  unsignedDayworks,
  outstandingSnags,
  rfis,
  enquiryDeals,
  userId,
  maxRows = 8,
  windowDays = 7,
  siteOnly = false,
}: ActionQueueProps) {
  const [expanded, setExpanded] = useState(false);
  const [showMilestones, setShowMilestones] = useComingUpMilestonesPreference(userId);
  const listId = useId();
  const today = new Date();
  const chaseEvents = useVariationChases(applications, projects);
  // Field-only members get tasks and site dates, never commercial rows.
  const enquiryProjectIds = useMemo(() => (siteOnly ? undefined : projectsWithProcurementAccess(projects)), [siteOnly, projects]);
  const items = buildActionItems({ chaseEvents, enquiryProjectIds, tasks, contractItems, applications, projects, siteCards, subcontractorCover, plantAssets, cisReturns, expenseClaims, salesInvoices, invoiceChases, unsignedDayworks, outstandingSnags, rfis, enquiryDeals, windowDays, includeMilestones: showMilestones, siteOnly, now: today });

  const isPhone = useMediaQuery('(max-width: 767px)');
  const visible = expanded ? items : items.slice(0, maxRows);
  const overflow = items.length - maxRows;
  const hasMore = overflow > 0;

  const toggleMilestones = () => {
    const next = !showMilestones;
    setShowMilestones(next);
  };
  const milestoneToggle = (
    <button
      type="button"
      role="switch"
      aria-checked={showMilestones}
      onClick={toggleMilestones}
      className="min-h-11 w-full shrink-0 border-t border-border/20 px-3 py-2 text-left text-sm text-muted-foreground font-apple hover:bg-muted/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]"
    >
      {showMilestones ? 'Hide project milestones' : 'Include project milestones'}
    </button>
  );

  if (isPhone) {
    return (
      <ComingUpList
        items={items}
        windowDays={windowDays}
        maxRows={maxRows}
        onOpen={(item) => item.target && openTarget(item.target)}
        milestones={{ shown: showMilestones, onToggle: toggleMilestones }}
      />
    );
  }

  if (visible.length === 0) {
    return (
      <div className="flex min-h-0 flex-col">
        <div className="flex-1 px-3 py-6 text-center text-[12px] text-muted-foreground font-apple">
          Nothing due in the next {windowDays} days.
        </div>
        {milestoneToggle}
      </div>
    );
  }

  // Overdue items sit outside the upcoming window promise, so they get
  // their own section instead of silently leading the upcoming list.
  const labelTones = { overdue: 'text-status-danger', upcoming: 'text-muted-foreground' } as const;
  const sections = comingUpSections(visible).map((section) => ({ ...section, labelTone: labelTones[section.key] }));

  return (
    <div className="relative flex min-h-0 flex-col">
      <div id={listId} className="font-apple">
        {sections.map((section) => (
          <section key={section.key}>
            <h3
              className={`px-3 pt-3 pb-1.5 text-xs font-medium uppercase tracking-[0.12em] ${section.labelTone}`}
            >
              {section.label}
            </h3>
            <ul>
              {section.rows.map((it) => {
                const cue = comingUpDueCue(it, today);
                const dueLabel = cue.label;
                const dueTone = {
                  danger: 'text-status-danger',
                  warning: 'text-status-warning',
                  muted: 'text-muted-foreground',
                }[cue.tone];
                const body = (
                  <>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate leading-tight">{it.title}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{it.context}</p>
                    </div>
                    <span className={`text-xs tabular-nums shrink-0 ${dueTone}`}>{dueLabel}</span>
                  </>
                );
                const rowClass = 'flex min-h-12 w-full items-center gap-3 px-3 py-2.5 text-left @[700px]/dash:px-4';
                return (
                  <li key={it.id} className="border-b border-border/20 last:border-b-0">
                    {it.target ? (
                      <button
                        type="button"
                        onClick={() => openTarget(it.target as ActionTarget)}
                        className={`${rowClass} cursor-pointer hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none`}
                      >
                        {body}
                      </button>
                    ) : (
                      <div className={rowClass}>{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      {hasMore && !expanded && (
        <div
          className="pointer-events-none absolute inset-x-0 h-6 bg-gradient-to-t from-card to-transparent"
          style={{ bottom: '44px' }}
        />
      )}

      {hasMore && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded(value => !value)}
          className="min-h-11 w-full shrink-0 border-t border-border/20 px-3 py-2 text-left text-sm font-medium text-foreground font-apple hover:bg-muted/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]"
        >
          {expanded ? 'Show fewer items' : `Show ${overflow} more due`}
        </button>
      )}

      {milestoneToggle}
    </div>
  );
}
